import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./ooraTrafficDataset.css";
import { getOoraJamPatchList, liveSyncOoraTraffic } from "../../Redux/Actions/ooraTrafficeDatasetActions";
import {
  WEEKDAYS,
  formatSlotLabel,
  formatDuration,
  formatDistance,
  formatPercent,
} from "./ooraTrafficDatasetHelpers";
import OoraJamPatchMap, { patchRowId } from "./ooraJamPatchesMap";

const LIMIT = 25;

const TABS = [
  { key: "LIVE", label: "Live" },
  { key: "PREDICTED", label: "Predicted" },
  { key: "TYPICAL", label: "Typical" },
];

const PRED_SLOTS = ["07:00", "09:00", "11:00", "13:00", "15:00", "17:00", "19:00", "21:00"];
// 06:00, 06:30 ... 23:30
const BUCKETS = Array.from({ length: 36 }, (_, i) => {
  const m = 6 * 60 + i * 30;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${m % 60 ? "30" : "00"}`;
});

const speedClass = (s) => `otd-level otd-level--${s === "TRAFFIC_JAM" ? "SEVERE" : "HEAVY"}`;

const OoraJamPatches = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ooraJamPatchList, patchLoading, liveSyncLoading } = useSelector(
    (state) => state.ooraTrafficDataset || {}
  );

  const list = ooraJamPatchList?.data?.list || [];
  const total = ooraJamPatchList?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const [tab, setTab] = useState("LIVE");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [day, setDay] = useState("0"); // live / predicted
  const [slot, setSlot] = useState(""); // predicted
  const [weekday, setWeekday] = useState(String(new Date().getDay())); // typical
  const [bucket, setBucket] = useState(""); // typical
  const [minFreq, setMinFreq] = useState("0"); // typical
  const [selectedId, setSelectedId] = useState(null);

  const isTypical = tab === "TYPICAL";

  const fetchList = useCallback(() => {
    const body = { page, limit: LIMIT };
    if (search) body.search = search;

    if (tab === "TYPICAL") {
      body.weekday = Number(weekday);
      if (bucket) body.bucket = bucket;
      body.min_frequency = Number(minFreq);
      dispatch(getOoraJamPatchList(body, "typical"));
    } else {
      body.source = tab;
      body.day = Number(day);
      if (tab === "PREDICTED" && slot) body.slot = slot;
      dispatch(getOoraJamPatchList(body, "patches"));
    }
  }, [dispatch, page, search, tab, day, slot, weekday, bucket, minFreq]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const changeTab = (key) => {
    setTab(key);
    setPage(1);
    setSelectedId(null);
    setSlot("");
    setDay(key === "PREDICTED" ? "1" : "0"); // predicted starts on tomorrow, live on today
  };

  const withReset = (setter) => (e) => {
    setPage(1);
    setSelectedId(null);
    setter(e.target.value);
  };

  const handleLiveSync = async () => {
    const response = await dispatch(liveSyncOoraTraffic({}));
    if (response?.statusCode === 200) {
      const r = response.data || {};
      if (r.skipped) toast(r.reason || "A live run is already running", { icon: "ℹ️" });
      else toast.success(`Live reading done: ${r.patches} jam patches from ${r.read} routes`);
      setPage(1);
      fetchList();
    } else {
      toast.error(response?.message || "Something went wrong");
    }
  };

  return (
    <div className="otd-wrapper">
      <div className="otd-header">
        <div className="otd-header-left">
          <div className="otd-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 17l6-6 4 4 8-8" />
              <polyline points="14 7 21 7 21 14" />
            </svg>
          </div>
          <div>
            <h1 className="otd-title">Jam Patches</h1>
            <p className="otd-subtitle">Exact jammed stretches, not whole roads</p>
          </div>
        </div>
        <div className="otd-actions">
          <button type="button" className="otd-btn otd-btn--ghost" onClick={() => navigate("/oora-traffic-dataset")}>
            Back
          </button>
          <button type="button" className="otd-btn otd-btn--submit" onClick={handleLiveSync} disabled={liveSyncLoading}>
            {liveSyncLoading ? "Reading traffic..." : "Take live reading now"}
          </button>
        </div>
      </div>

      <div className="otd-card">
        <div className="otd-toolbar">
          <div className="otd-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`otd-tab ${tab === t.key ? "otd-tab--active" : ""}`}
                onClick={() => changeTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <span className="otd-count">{total} patch{total === 1 ? "" : "es"}</span>
        </div>

        <div className="otd-toolbar">
          <div className="otd-toolbar-left">
            <div className="otd-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by road"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            {!isTypical && (
              <select className="otd-input otd-date-filter" value={day} onChange={withReset(setDay)}>
                <option value="0">Today</option>
                <option value="1">Tomorrow</option>
              </select>
            )}

            {tab === "PREDICTED" && (
              <select className="otd-input otd-date-filter" value={slot} onChange={withReset(setSlot)}>
                <option value="">All slots</option>
                {PRED_SLOTS.map((s) => (
                  <option key={s} value={s}>{formatSlotLabel(s)}</option>
                ))}
              </select>
            )}

            {isTypical && (
              <>
                <select className="otd-input otd-date-filter" value={weekday} onChange={withReset(setWeekday)}>
                  {WEEKDAYS.map((w, i) => (
                    <option key={w} value={i}>{w}</option>
                  ))}
                </select>
                <select className="otd-input otd-date-filter" value={bucket} onChange={withReset(setBucket)}>
                  <option value="">All times</option>
                  {BUCKETS.map((b) => (
                    <option key={b} value={b}>{formatSlotLabel(b)}</option>
                  ))}
                </select>
                <select className="otd-input otd-date-filter" value={minFreq} onChange={withReset(setMinFreq)}>
                  <option value="0">Any frequency</option>
                  <option value="0.25">Seen 25%+ of days</option>
                  <option value="0.5">Seen 50%+ of days</option>
                  <option value="0.75">Seen 75%+ of days</option>
                </select>
              </>
            )}
          </div>
        </div>

        {/* MAP: shows the patches of the current page */}
        <div style={{ padding: "16px 24px 0" }}>
          <OoraJamPatchMap patches={list} selectedId={selectedId} onSelect={setSelectedId} />
          <p className="otd-hint" style={{ marginTop: 8 }}>
            <span className="otd-legend otd-legend--jam" /> Jam &nbsp;
            <span className="otd-legend otd-legend--slow" /> Slow &nbsp;· click a line or a row to highlight it
          </p>
        </div>

        <div className="otd-table-wrap">
          <table className="otd-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>Road</th>
                <th style={{ width: 90 }}>Type</th>
                <th style={{ width: 110 }}>{isTypical ? "Avg length" : "Length"}</th>
                {isTypical ? (
                  <>
                    <th style={{ width: 110 }}>Max length</th>
                    <th style={{ width: 110 }}>Time</th>
                    <th style={{ width: 130 }}>Seen</th>
                    <th style={{ width: 110 }}>Frequency</th>
                  </>
                ) : (
                  <>
                    <th style={{ width: 110 }}>Slot (IST)</th>
                    <th style={{ width: 110 }}>Delay</th>
                    <th style={{ width: 110 }}>Routes</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {patchLoading ? (
                <tr><td colSpan={8} className="otd-empty">Loading...</td></tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={8} className="otd-empty">
                    {isTypical
                      ? "No typical patches yet. They appear after live readings have run for a few weeks."
                      : "No jam patches found"}
                  </td>
                </tr>
              ) : (
                list.map((p, i) => {
                  const id = patchRowId(p);
                  return (
                    <tr
                      key={id}
                      className="otd-slot-row"
                      style={selectedId === id ? { background: "var(--otd-accent-light)" } : undefined}
                      onClick={() => setSelectedId(id)}
                    >
                      <td className="otd-mono">{(page - 1) * LIMIT + i + 1}</td>
                      <td className="otd-row-title">{p.road_name || "Unnamed road"}</td>
                      <td><span className={speedClass(p.speed)}>{p.speed === "TRAFFIC_JAM" ? "JAM" : "SLOW"}</span></td>
                      <td>{formatDistance(isTypical ? p.avg_jam_length_meters : p.jam_length_meters)}</td>
                      {isTypical ? (
                        <>
                          <td>{formatDistance(p.max_jam_length_meters)}</td>
                          <td>{formatSlotLabel(p.bucket)}</td>
                          <td className="otd-mono">{p.days_seen} of {p.runs_total} days</td>
                          <td><span className="otd-peak">{formatPercent(p.frequency)}</span></td>
                        </>
                      ) : (
                        <>
                          <td>{formatSlotLabel(p.slot)}</td>
                          <td>{p.delay_sec ? formatDuration(p.delay_sec) : "-"}</td>
                          <td className="otd-mono">{p.routes_affected ?? "-"}</td>
                        </>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="otd-pagination">
          <span className="otd-page-info">Page {page} of {totalPages}</span>
          <div className="otd-page-btns">
            <button
              type="button"
              className="otd-btn otd-btn--ghost"
              disabled={page <= 1 || patchLoading}
              onClick={() => { setSelectedId(null); setPage((p) => p - 1); }}
            >
              Previous
            </button>
            <button
              type="button"
              className="otd-btn otd-btn--ghost"
              disabled={page >= totalPages || patchLoading}
              onClick={() => { setSelectedId(null); setPage((p) => p + 1); }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OoraJamPatches;